/**
 * Authentication context for Ops Dashboard.
 * Uses Cognito for authentication and checks admin group membership.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  AuthUser,
} from 'aws-amplify/auth';
import { fetchConfig } from '../services/opsApi';

interface User {
  email: string;
  userId: string;
  groups: string[];
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminGroupName, setAdminGroupName] = useState<string | null>(null);

  // Fetch admin group name from API
  // MED-044 FIX: Fetch admin group name from API instead of hardcoding
  const loadAdminGroupName = useCallback(async () => {
    try {
      const config = await fetchConfig();
      setAdminGroupName(config.admin_group_name);
      return config.admin_group_name;
    } catch (err) {
      // Fallback to default if API call fails
      console.warn('Failed to fetch admin group name from API, using default:', err);
      const defaultName = 'roboticks-ops-admins';
      setAdminGroupName(defaultName);
      return defaultName;
    }
  }, []);

  // Check current authentication state
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const authUser: AuthUser = await getCurrentUser();
      const session = await fetchAuthSession();

      // Extract user info from tokens
      const idToken = session.tokens?.idToken;
      const payload = idToken?.payload || {};

      const email = (payload.email as string) || authUser.signInDetails?.loginId || '';
      const groups = (payload['cognito:groups'] as string[]) || [];

      // Fetch admin group name if not loaded yet (user is authenticated now)
      const currentAdminGroupName = adminGroupName || await loadAdminGroupName();
      const isAdmin = groups.includes(currentAdminGroupName);

      setUser({
        email,
        userId: authUser.userId,
        groups,
        isAdmin,
      });
    } catch (err) {
      // Not authenticated
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [adminGroupName, loadAdminGroupName]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      await signIn({ username: email, password });
      await checkAuth();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to sign out. Please try again.';
      setError(message);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch {
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    error,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
